import { useState, type JSX } from "react";
import { useStepCategory } from "./useStepCategory";
import { useStepEmployee } from "./useStepEmployee";
import { useStepPeriod } from "./useStepPeriod";
import { StepCategory } from "../components/StepCategory";
import { StepPeriod } from "../components/StepPeriod";
import { StepEmployee } from "../components/StepEmployee";
import { StepResume } from "../components/StepResume";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { executeService } from "../services/service";
import { usePermisoReportes } from "../../../../auth/permisosModulos";

interface useStepperReturn
  extends
  ReturnType<typeof useStepCategory>,
  ReturnType<typeof useStepPeriod>,
  ReturnType<typeof useStepEmployee> {
  t: TFunction;
  canExecuteReport: boolean;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  showStepContent: (
    step: number,
    props: ReturnType<typeof useStepper>,
  ) => JSX.Element | null;
  nextStep: () => void;
  disableNextStep: () => boolean;
}

const useStepper = (): useStepperReturn => {
  const { t } = useTranslation("execute_report.stepper");

  const [activeStep, setActiveStep] = useState<number>(0);
  const canExecuteReport = usePermisoReportes("ejecutar-reporte");

  const stepCategoryProps = useStepCategory({ activeStep });
  const stepPeriodProps = useStepPeriod({ activeStep });
  const stepEmployeeProps = useStepEmployee({
    t,
    activeStep,
    period: stepPeriodProps.range,
  });

  const showStepContent = (
    step: number,
    props: ReturnType<typeof useStepper>,
  ) => {
    switch (step) {
      case 0:
        return <StepCategory {...props} />;
      case 1:
        return <StepPeriod {...props} />;
      case 2:
        return <StepEmployee {...props} />;
      case 3:
        return <StepResume {...props} />;
      default:
        return null;
    }
  };

  const nextStep = async () => {
    let queryParams = new URLSearchParams(window.location.search);
    let newUrl = `${window.location.pathname}?${queryParams.toString()}`;

    switch (activeStep) {
      case 0:
        // agregame un query params en la url reports= con los ids de los reportes seleccionados
        queryParams = new URLSearchParams(window.location.search);

        const selectedReportIds = stepCategoryProps.selectedIds
          .filter((item) => item.toString().startsWith("report-"))
          .map((item) => item.toString().replace("report-", ""));

        queryParams.set("reports", selectedReportIds.join(","));
        newUrl = `${window.location.pathname}?${queryParams.toString()}`;

        window.history.replaceState(null, "", newUrl);

        setActiveStep(1);
        break;
      case 1:
        // agregame un query params en la url start_date= y end_date= con las fechas seleccionadas
        queryParams = new URLSearchParams(window.location.search);

        const startDate = stepPeriodProps.range[0]
          ? stepPeriodProps.range[0].format("YYYY-MM-DD")
          : "";
        const endDate = stepPeriodProps.range[1]
          ? stepPeriodProps.range[1].format("YYYY-MM-DD")
          : "";

        queryParams.set("start_date", startDate);
        queryParams.set("end_date", endDate);
        newUrl = `${window.location.pathname}?${queryParams.toString()}`;

        window.history.replaceState(null, "", newUrl);

        setActiveStep(2);
        break;
      case 2:
        //agregame un query params en la url employees= con los ids de los empleados seleccionados
        queryParams = new URLSearchParams(window.location.search);
        const selectedEmployeeIds = stepEmployeeProps.getSelectedEmployeeIds();

        const filteredEmployeeIds = selectedEmployeeIds.filter(
          (id) => id && !isNaN(Number(id)),
        );

        queryParams.set("employees", filteredEmployeeIds.join(","));
        newUrl = `${window.location.pathname}?${queryParams.toString()}`;

        window.history.replaceState(null, "", newUrl);

        setActiveStep(3);
        break;
      case 3:
        /* const token = localStorage.getItem("token");
        const decodedToken = token
          ? JSON.parse(atob(token.split(".")[1]))
          : null; */
        const configs: { key: string; value: string }[] = JSON.parse(
          localStorage.getItem("config_system") || "[]",
        );

        const userId = localStorage.getItem("user_id") || "";

        const promises = stepCategoryProps.selectedIds
          .filter((item) => item.toString().startsWith("report-"))
          .map((item) => {
            const reportSlug =
              stepCategoryProps.categories
                ?.flatMap((category) => category.children ?? [])
                .find((r) => r.id === item.toString())?.className || "";

            const employeeIds = stepEmployeeProps
              .getSelectedEmployeeIds()
              .filter((id) => id && !isNaN(Number(id)))
              .map((id) => id.toString());

            const personalEmployeeIds = stepEmployeeProps.rows
              .filter((row) => employeeIds.includes(row.id.toString()))
              .map((row) => row.personal_number);

            const body = {
              file: true,
              external_user_id: userId,
              reportData: {
                company_id:
                  configs.find((config) => config.key === "company_id")
                    ?.value || "",
                employees: personalEmployeeIds,
                report_slug: reportSlug,
                dateRange: `${stepPeriodProps.range[0]?.format("YYYY-MM-DD") || ""} - ${stepPeriodProps.range[1]?.format("YYYY-MM-DD") || ""}`,
              },
            };

            return executeService.generateReport({ body });
          });

        await Promise.all(promises)
          .then(() => {
            window.history.pushState(
              null,
              "",
              "reports?module=reports&view=results_report",
            );

            window.dispatchEvent(new PopStateEvent("popstate"));
          })
          .catch((error) => {
            console.error("Error generating reports", error);
          });
        break;
      default:
        break;
    }
  };

  const disableNextStep = () => {
    switch (activeStep) {
      case 0:
        const selectedReportIds = stepCategoryProps.selectedIds.filter((item) =>
          item.toString().startsWith("report-"),
        );

        return selectedReportIds.length === 0;
      case 1:
        if (
          stepPeriodProps.range[0] !== null &&
          stepPeriodProps.range[1] === null
        ) {
          stepPeriodProps.setRange([
            stepPeriodProps.range[0],
            stepPeriodProps.range[0],
          ]);
        }

        return (
          stepPeriodProps.range[0] === null && stepPeriodProps.range[1] === null
        );
      case 2:
        const selectedEmployeeIds = stepEmployeeProps.getSelectedEmployeeIds();
        return selectedEmployeeIds.length === 0;
      case 3:
        // false si no hay reportes seleccionados, si no hay fechas seleccionadas o si no hay empleados seleccionados
        return (
          stepCategoryProps.selectedIds.filter((item) =>
            item.toString().startsWith("report-"),
          ).length === 0 ||
          stepPeriodProps.range[0] === null ||
          stepPeriodProps.range[1] === null ||
          stepEmployeeProps.getSelectedEmployeeIds().length === 0
        );
      default:
        return true;
    }
  };

  return {
    ...stepCategoryProps,
    ...stepPeriodProps,
    ...stepEmployeeProps,
    t,
    canExecuteReport,
    activeStep,
    setActiveStep,
    showStepContent,
    nextStep,
    disableNextStep,
  };
};

export { useStepper };
